import React, { forwardRef } from 'react';
import clsx from 'clsx';
import useStyles from './style';
import { capitalize } from '@material-ui/core/utils';

type StatusBulletRef = HTMLElementTagNameMap[keyof HTMLElementTagNameMap];

export interface StatusBulletProps {
    /**
     * Override or extend the styles applied to the component.
     */
    className?: string;
    /**
     * The content of the element.
     */
    children: React.ReactNode;
    /**
     * The component used for the root node.
     * Either a string to use a HTML element or a component.
     */
    component?: React.ElementType;
    /**
     * If `true`, the element should be displayed in a disabled state.
     */
    disabled?: boolean;
    /**
     * If `true`, the element will appear clickable, and will raise when pressed,
     * even if the onClick prop is not defined.
     */
    clickable?: boolean;
    /**
     * The size of the element.
     */
    size?: 'medium' | 'small';
    /**
     * The severity of the alert. This defines the color and icon used.
     */
    color?: 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';
    /**
     * The severity of the alert. This defines the color and icon used.
     */
    variant?: 'filled' | 'dot';
    /**
     * @ignore
     */
    onClick?(event: React.MouseEvent<HTMLButtonElement>): void;
}

const StatusBullet = forwardRef<StatusBulletRef, StatusBulletProps>((props: StatusBulletProps, ref) => {
    const {
        className,
        clickable,
        component: Component = 'div',
        disabled,
        onClick,
        color = 'default',
        size = 'medium',
        variant = 'filled',
        ...rest
    } = props;

    const classes = useStyles();

    const rootClassName = clsx(
        {
            [classes.root]: true,
            [classes[variant]]: variant,
            [classes[`${variant}${capitalize(color)}`]]: color,
            [classes.sizeSmall]: size === 'small',
            [classes.clickable]: clickable !== false && onClick ? true : clickable,
            [classes.disabled]: disabled,
        },
        className,
    );

    return (
        <Component
            {...rest}
            aria-disabled={disabled ? true : undefined}
            className={rootClassName}
            onClick={onClick}
            ref={ref}
        />
    );
});

StatusBullet.displayName = 'StatusBullet';

export default StatusBullet;
